import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mist } from "../components/temple/Mist";
import { VortexOverlay } from "../components/temple/VortexOverlay";
import { playCosmicWarp } from "../lib/cosmicWarp";

const ENTRY_IMG =
  "https://customer-assets.emergentagent.com/job_crypto-mean/artifacts/b4vaedrw_file_0000000050e8722f8f7b76f5e02d75ca.png";

export default function EntryPage() {
  const navigate = useNavigate();
  const [transitioning, setTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Failsafe: if the transition animation fails / is skipped, still navigate.
  const enterTemple = (e) => {
    if (e) e.preventDefault();
    if (transitioning) return;
    playCosmicWarp();
    setTransitioning(true);
    // Hard navigate after animation completes (1.8s + 0.2s buffer)
    const safety = setTimeout(() => navigate("/temple"), 2000);
    // Cleanup if unmounted
    return () => clearTimeout(safety);
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-black"
      data-testid="entry-page"
    >
      {/* Background image — center-right on mobile to keep Kek-u visible */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${ENTRY_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: isMobile ? "78% center" : "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#06030a",
        }}
        data-testid="entry-bg"
      />

      {/* Dark vignette + readability overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background: isMobile
            ? "linear-gradient(180deg, rgba(6,3,10,0.55) 0%, rgba(6,3,10,0.35) 35%, rgba(6,3,10,0.7) 75%, rgba(6,3,10,0.92) 100%)"
            : "radial-gradient(ellipse at 30% 50%, rgba(6,3,10,0.45) 0%, rgba(6,3,10,0.78) 60%, rgba(6,3,10,0.92) 100%)",
        }}
      />

      {/* Slow purple mist */}
      <Mist />

      {/* Content wrapper — gets "sucked" into the vortex on transition */}
      <div
        className={`relative z-10 min-h-screen w-full flex flex-col ${
          transitioning ? "kek-sucked" : ""
        }`}
        data-testid="entry-content"
      >
        {/* Top spacer — title sits upper-center */}
        <div className="flex-1 flex flex-col items-center justify-end px-6 pt-16 sm:pt-24 text-center kek-no-select">
          <div className="kek-fade kek-fade-1 max-w-2xl">
            <p
              className="font-cinzel kek-gold-thin text-[0.65rem] sm:text-xs uppercase tracking-[0.5em] mb-4 sm:mb-6"
              data-testid="entry-pretitle"
            >
              The Temple Awakens
            </p>
            <h1
              className="font-cinzel kek-gold-text text-4xl sm:text-6xl lg:text-7xl font-bold tracking-[0.18em] uppercase"
              data-testid="entry-title"
            >
              Lord Kek-u
            </h1>
            <div className="kek-divider my-5 sm:my-7 mx-auto w-40 sm:w-60" />
          </div>

          <div className="kek-fade kek-fade-2 max-w-xl">
            <p
              className="font-cormorant italic text-lg sm:text-2xl text-[color:var(--kek-gold)] tracking-wide"
              data-testid="entry-subtitle"
            >
              Father of All Pepes
            </p>
          </div>
        </div>

        {/* Bottom half — tagline + button (button always centered horizontally) */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-20 sm:pb-24 text-center">
          <div className="kek-fade kek-fade-3 max-w-md sm:max-w-lg mb-8 sm:mb-12">
            <p
              className="font-cormorant text-base sm:text-xl leading-relaxed text-[color:var(--kek-text)]/85"
              data-testid="entry-tagline"
            >
              Pepe was born on the internet.
              <br />
              <span className="kek-gold-thin">Kek-u came before the internet.</span>
            </p>
          </div>

          <div className="kek-fade kek-fade-4">
            <button
              type="button"
              onClick={enterTemple}
              className="kek-btn kek-btn--lg"
              data-testid="enter-temple-btn"
              aria-label="Enter the Temple"
            >
              Enter the Temple
            </button>
          </div>
        </div>
      </div>

      {/* Cinematic vortex transition */}
      {transitioning && <VortexOverlay />}
    </div>
  );
}
